import { useCallback, useEffect, useMemo, useState } from "react";
import { relatorioAdmin } from "../../servicos/relatorioAdmin";
import { filiadoAPI } from "../../servicos/filiado";
import ListagemFeedback from "../Listagem/feedback";
import ListagemOpcoes from "../Listagem/opcoes";
import Topo from "../Topo/topo";
import Paginacao from "../Listagem/paginacao";
import { formatarMoedaBRL, normalizarValorNumerico } from "../../constantes/relatorios";
import { DataTable } from "../ui/data-table";
import ColumnToggleDropdown from "../ui/column-toggle-dropdown";
import { exportarXLSX } from "../../utilitarios/excel";

function TabelaRelatorioEmplacamento() {

    const date = new Date();
    const STATUS_FINALIZADO = 'Finalizado';
    const STATUS_NAO_FINALIZADO = 'Não Finalizado';

    const breadcrumbs = ['Admin','Rel. Emplacamento'];
    const [PesquisaRelatorioEmplacamento, setPesquisaRelatorioEmplacamento] = useState([]);
    const [PesquisaFiliados, setPesquisaFiliados] = useState([]);
    const [totalTaxa, setTotalTaxa] = useState(0);
    const [filtroTexto, setFiltroTexto] = useState('');

    const [exibeErro, setExibeErro] = useState(false);    
    const [mensagemErro, setMensagemErro] = useState('');    

    const [fi100_key_public, set100_key_public] = useState('0');
    const [mes, setmes] = useState(date.getMonth()+1);
    const [ano, setano] = useState(String(date.getFullYear()));
    const [anosDisponiveis, setAnosDisponiveis] = useState([]);

    const possuiValor = useCallback((valor) => {
        if (valor === null || valor === undefined) {
            return false;
        }
        if (typeof valor === 'string') {
            return valor.trim().length > 0;
        }
        return true;
    }, []);

    const valorOuTraco = useCallback(
        (valor) => (possuiValor(valor) ? String(valor) : '-'),
        [possuiValor],
    );

    const ehFinalizado = useCallback(
        (status) => String(status || '').trim().toLowerCase() === STATUS_FINALIZADO.toLowerCase(),
        [STATUS_FINALIZADO],
    );

    const obterStatusEmplacamento = useCallback((registro) => {
        if (possuiValor(registro?.status)) {
            return registro.status;
        }
        if (possuiValor(registro?.ve101_url_seguranca)) {
            return STATUS_FINALIZADO;
        }
        return STATUS_NAO_FINALIZADO;
    }, [possuiValor]);

    const ordenarFinalizadosPrimeiro = useCallback(
        (registros = []) =>
            registros
                .map((item, indiceOriginal) => ({ item, indiceOriginal }))
                .sort((a, b) => {
                    const prioridadeA = ehFinalizado(a.item?.status) ? 0 : 1;
                    const prioridadeB = ehFinalizado(b.item?.status) ? 0 : 1;
                    if (prioridadeA !== prioridadeB) {
                        return prioridadeA - prioridadeB;
                    }
                    return a.indiceOriginal - b.indiceOriginal;
                })
                .map(({ item }) => item),
        [ehFinalizado],
    );

    const normalizarRegistrosEmplacamento = useCallback(
        (registros = []) =>
            ordenarFinalizadosPrimeiro(
                (Array.isArray(registros) ? registros : []).map((item) => ({
                    ...item,
                    status: obterStatusEmplacamento(item),
                })),
            ),
        [obterStatusEmplacamento, ordenarFinalizadosPrimeiro],
    );

    const obterCnpjFiliado = useCallback(
        (registro) => valorOuTraco(registro?.fi100_filiado?.fi100_num_cnpj || registro?.fi100_num_cnpj),
        [valorOuTraco],
    );

    const formatarDescricaoFiliado = useCallback((registro) => {
        const nome = registro?.fi100_filiado?.fi100_dsc_filiado || registro?.fi100_dsc_filiado || '-';
        const associadoRaw = registro?.fi100_filiado?.fi100_ind_associado ?? registro?.fi100_ind_associado ?? false;
        const associadoTexto = associadoRaw ? 'É associado' : 'Não é associado';
        return `${nome} - ${associadoTexto}`;
    }, []);

    useEffect(() => {
        fetchFiliados();
    }, []);

    useEffect(() => {
        fetchEmplacamentos(fi100_key_public, mes, ano);
    }, [fi100_key_public, mes, ano]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchAnosEmplacamento(fi100_key_public);
    }, [fi100_key_public]);

    useEffect(() => {
        const anoAtual = String(ano);
        if (anosDisponiveis.length > 0 && !anosDisponiveis.includes(anoAtual)) {
            setano(anosDisponiveis[anosDisponiveis.length - 1]);
        }
    }, [anosDisponiveis, ano]);

    async function fetchEmplacamentos(fKey,fMes,fAno){

        relatorioAdmin.get(`/emplacamento/${fKey}/${fMes}/${fAno}`)
        .then(resposta => {
            const registros = normalizarRegistrosEmplacamento(resposta.data);
            setPesquisaRelatorioEmplacamento(registros);
            const taxaTotal = registros.reduce((acc, item) => {
                const bruto = item?.fi100_vlr_emplacamento ?? item?.fi100_filiado?.fi100_vlr_emplacamento ?? 0;
                return acc + normalizarValorNumerico(bruto);
            }, 0);
            setTotalTaxa(taxaTotal);
        })
        .catch(erro => {          
            setTotalTaxa(0);
            if(erro?.resposta?.data?.message){
                setExibeErro(true);
                setMensagemErro(erro.resposta.data.message);
            }else{
                if (erro.response?.status === 401) {
                    setExibeErro(true);
                    setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                  }else{
                    setExibeErro(true);
                    setMensagemErro('Ops! Ocorreu um erro inesperado.');
                  }                                           
            }        
        })               
        
    }        

    async function fetchAnosEmplacamento(fKey){
        relatorioAdmin.get(`/emplacamento/anos/${fKey}`)
        .then(resposta => {
            const anos = (resposta.data?.anos || []).map((item) => String(item));
            setAnosDisponiveis(anos);
        })
        .catch(erro => {
            setAnosDisponiveis([]);
            if(erro?.resposta?.data?.message){
                setExibeErro(true);
                setMensagemErro(erro.resposta.data.message);
            }else{
                if (erro.response?.status === 401) {
                    setExibeErro(true);
                    setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                  }else{
                    setExibeErro(true);
                    setMensagemErro('Ops! Ocorreu um erro inesperado.');
                  }
            }
        })
    }

    async function fetchFiliados(){

        filiadoAPI.get('/')
        .then(resposta => {
            setPesquisaFiliados(resposta.data);
        })
        .catch(erro => {          
            setTotalTaxa(0);
            if(erro?.resposta?.data?.message){
                setExibeErro(true);
                setMensagemErro(erro.resposta.data.message);
            }else{
                if (erro.response?.status === 401) {
                    setExibeErro(true);
                    setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                  }else{
                    setExibeErro(true);
                    setMensagemErro('Ops! Ocorreu um erro inesperado.');
                  }                                           
            }        
        })               
        
    }        


    const FormatarDataHora = useCallback((string) => {

        let retorno = '-';
        if(possuiValor(string)){
            const texto = String(string);
            const partes = texto.split("T");
            if (partes.length >= 2) {
                const dataaux = partes[0].split("-");
                if (dataaux.length === 3) {
                    const dataformatada = dataaux[2] + '/' + dataaux[1] + "/" + dataaux[0];
                    retorno = `${dataformatada} ${partes[1].substring(0,5)}`;
                }
            }
        }

        return retorno;

    }, [possuiValor]);

    const colunasBase = useMemo(() => [
        {
            id: 'filiado',
            header: () => 'Filiado',
            accessorFn: (row) => formatarDescricaoFiliado(row),
            className: 'tw-align-top tw-font-medium tw-text-slate-700 dark:tw-text-slate-200',
            label: 'Filiado',
            canHide: false,
        },
        {
            id: 'cnpj',
            header: () => 'CNPJ',
            accessorFn: (row) => obterCnpjFiliado(row),
            className: 'tw-whitespace-nowrap tw-text-slate-600 dark:tw-text-slate-300',
            label: 'CNPJ',
        },
        {
            id: 'placa',
            header: () => 'Placa',
            accessorKey: 've101_dsc_placa',
            cell: ({ value }) => valorOuTraco(value),
            className: 'tw-whitespace-nowrap tw-uppercase tw-font-semibold tw-text-slate-700 dark:tw-text-slate-100',
            label: 'Placa',
        },
        {
            id: 'chassi',
            header: () => 'Chassi',
            accessorKey: 've101_dsc_chassi',
            cell: ({ value }) => valorOuTraco(value),
            label: 'Chassi',
        },
        {
            id: 'renavam',
            header: () => 'Renavam',
            accessorKey: 've101_num_renavam',
            cell: ({ value }) => valorOuTraco(value),
            label: 'Renavam',
        },
        {
            id: 'emplacamento',
            header: () => 'Emplacamento',
            accessorKey: 've101_cod_emplacar',
            cell: ({ value }) => valorOuTraco(value),
            label: 'Emplacamento',
        },
        {
            id: 'taxa',
            header: () => 'Taxa por Emplacamento',
            accessorFn: (row) => normalizarValorNumerico(row?.fi100_vlr_emplacamento ?? row?.fi100_filiado?.fi100_vlr_emplacamento ?? 0),
            cell: ({ value }) => formatarMoedaBRL(normalizarValorNumerico(value)),
            className: 'tw-whitespace-nowrap',
            label: 'Taxa',
        },
        {
            id: 'processo',
            header: () => 'Processo',
            accessorKey: 've101_num_processo',
            cell: ({ value }) => valorOuTraco(value),
            label: 'Processo',
        },
        {
            id: 'dataCadastro',
            header: () => 'Data Cadastro',
            accessorFn: (row) => row.createdAt,
            cell: ({ value }) => FormatarDataHora(value),
            label: 'Data Cadastro',
        },
        {
            id: 'status',
            header: () => 'Status',
            accessorFn: (row) => obterStatusEmplacamento(row),
            cell: ({ value }) => valorOuTraco(value),
            className: 'tw-whitespace-nowrap',
            label: 'Status',
        },
        {
            id: 'situacao',
            header: () => 'Situação',
            accessorKey: 've101_dsc_situacao',
            cell: ({ value }) => valorOuTraco(value),
            label: 'Situação',
        },
        {
            id: 'dataSituacao',
            header: () => 'Data Situação',
            accessorFn: (row) => row.ve101_dat_situacao,
            cell: ({ value }) => FormatarDataHora(value),
            label: 'Data Situação',
        },
    ], [FormatarDataHora, formatarDescricaoFiliado, obterCnpjFiliado, obterStatusEmplacamento, valorOuTraco]);

    const [colunasVisiveis, setColunasVisiveis] = useState(() =>
        Object.fromEntries(colunasBase.map(coluna => [coluna.id, true])),
    );

    const colunasTabela = useMemo(
        () => colunasBase.filter(coluna => colunasVisiveis[coluna.id] !== false),
        [colunasBase, colunasVisiveis],
    );

    const cabecalhoExportacao = useMemo(
        () => [
            'Filiado',
            'CNPJ',
            'Placa',
            'Chassi',
            'Renavam',
            'Emplacamento',
            'Taxa por Emplacamento',
            'Processo',
            'Data Cadastro',
            'Status',
            'Situação',
            'Data Situação',
        ],
        [],
    );

    const dadosFiltrados = useMemo(() => {
        const busca = filtroTexto.trim().toLowerCase();
        if (!busca) {
            return PesquisaRelatorioEmplacamento;
        }
        return PesquisaRelatorioEmplacamento.filter((item) => {
            const termos = [
                item?.fi100_filiado?.fi100_dsc_filiado,
                item?.fi100_filiado?.fi100_num_cnpj,
                item?.ve101_dsc_placa,
                item?.ve101_dsc_chassi,
                item?.ve101_num_renavam,
                item?.ve101_cod_emplacar,
                item?.ve101_num_processo,
                obterStatusEmplacamento(item),
                item?.ve101_dsc_situacao,
            ];
            return termos.some((campo) => (campo || '').toString().toLowerCase().includes(busca));
        });
    }, [PesquisaRelatorioEmplacamento, filtroTexto, obterStatusEmplacamento]);

    const totalTaxaFiltrado = useMemo(
        () =>
            dadosFiltrados.reduce((acc, item) => {
                const bruto = item?.fi100_vlr_emplacamento ?? item?.fi100_filiado?.fi100_vlr_emplacamento ?? 0;
                return acc + normalizarValorNumerico(bruto);
            }, 0),
        [dadosFiltrados],
    );

    const toggleColuna = (colunaId) => {
        const coluna = colunasBase.find(col => col.id === colunaId);
        if (coluna?.canHide === false) {
            return;
        }
        setColunasVisiveis((anteriores) => ({
            ...anteriores,
            [colunaId]: anteriores[colunaId] === false,
        }));
    };

    const handleExportarExcel = () => {
        const linhas = PesquisaRelatorioEmplacamento.map((emplacamento) => [
            formatarDescricaoFiliado(emplacamento),
            obterCnpjFiliado(emplacamento),
            emplacamento.ve101_dsc_placa || '',
            emplacamento.ve101_dsc_chassi || '',
            emplacamento.ve101_num_renavam || '',
            emplacamento.ve101_cod_emplacar || '',
            normalizarValorNumerico(emplacamento?.fi100_vlr_emplacamento ?? emplacamento?.fi100_filiado?.fi100_vlr_emplacamento ?? 0),
            valorOuTraco(emplacamento.ve101_num_processo),
            FormatarDataHora(emplacamento.createdAt),
            obterStatusEmplacamento(emplacamento),
            valorOuTraco(emplacamento.ve101_dsc_situacao),
            FormatarDataHora(emplacamento.ve101_dat_situacao),
        ]);

        linhas.push([
            'Totais',
            '',
            '',
            '',
            '',
            '',
            normalizarValorNumerico(totalTaxa),
            '',
            '',
            '',
            '',
            '',
        ]);

        exportarXLSX({
            arquivo: 'ConexaoDetranPR_Emplacamento',
            aba: 'Emplacamento',
            cabecalho: cabecalhoExportacao,
            linhas,
            colunasNumericas: [6],
        });
    };

    return (
        <div class="row">
            <div class="container">
                <Topo titulo='Relatório Emplacamento' breadcrumbs={breadcrumbs} btnincluir={false} admin={true}/>
                <ListagemFeedback
                    displayErro={exibeErro}
                    mensagemErro={mensagemErro}
                />
            </div>
            <div class="admin-emplacar-card tw-mt-4 tw-rounded-3xl tw-border tw-border-slate-200/70 tw-bg-white/90 tw-p-6 tw-shadow-xl tw-backdrop-blur-sm dark:tw-border-slate-800/60 dark:tw-bg-slate-900/60 dark:tw-shadow-[0_20px_45px_rgba(8,15,32,0.45)]">
            <ListagemOpcoes
                esconderTamanhoPagina={true}
                esconderOpcaoFiltro={true}
                esconderCampoFiltro={true}
            />
            <div class="row row-padding-conteudo tw-pt-2">
                <div class="col-12">
                    <div class="tw-flex tw-flex-col tw-gap-5 tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/90 tw-p-5 tw-shadow-sm tw-backdrop-blur dark:tw-border-slate-700/70 dark:tw-bg-slate-900/70 dark:tw-shadow-[0_20px_30px_rgba(8,15,32,0.35)]">
                        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                            <div class="tw-flex tw-items-center tw-gap-3">
                                <label for="fi100_key_public" class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-white tw-leading-none">
                                    Filiado:
                                </label>
                                <select
                                    class="form-select tw-h-11 tw-min-w-[260px] tw-border-slate-200/70 tw-bg-white/95 dark:tw-border-slate-700 dark:tw-text-white dark:tw-!bg-slate-900/70"
                                    aria-label="Selecione"
                                    id="fi100_key_public"
                                    name="fi100_key_public"
                                    onChange={evento => { set100_key_public(evento.target.value); fetchEmplacamentos(evento.target.value, mes, ano); }}
                                    value={fi100_key_public}
                                >
                                    <option value="0">Selecione</option>
                                    {PesquisaFiliados.map(filiado => (
                                        <option value={filiado.fi100_key_public}>{filiado.fi100_dsc_filiado}</option>
                                    ))}
                                </select>
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-3">
                                <label for="mes" class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-white tw-leading-none">
                                    Mês:
                                </label>
                                <select
                                    class="form-select tw-h-11 tw-min-w-[140px] tw-border-slate-200/70 tw-bg-white/95 dark:tw-border-slate-700 dark:tw-text-white dark:tw-!bg-slate-900/70"
                                    aria-label="Selecione"
                                    id="mes"
                                    name="mes"
                                    onChange={evento => { setmes(evento.target.value); fetchEmplacamentos(fi100_key_public, evento.target.value, ano); }}
                                    value={mes}
                                >
                                    <option value="1">Janeiro</option>
                                    <option value="2">Fevereiro</option>
                                    <option value="3">Março</option>
                                    <option value="4">Abril</option>
                                    <option value="5">Maio</option>
                                    <option value="6">Junho</option>
                                    <option value="7">Julho</option>
                                    <option value="8">Agosto</option>
                                    <option value="9">Setembro</option>
                                    <option value="10">Outubro</option>
                                    <option value="11">Novembro</option>
                                    <option value="12">Dezembro</option>
                                </select>
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-3">
                                <label for="ano" class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-white tw-leading-none">
                                    Ano:
                                </label>
                                <select
                                    class="form-select tw-h-11 tw-min-w-[120px] tw-border-slate-200/70 tw-bg-white/95 dark:tw-border-slate-700 dark:tw-text-white dark:tw-!bg-slate-900/70"
                                    aria-label="Selecione"
                                    id="ano"
                                    name="ano"
                                    onChange={evento => { setano(evento.target.value); fetchEmplacamentos(fi100_key_public, mes, evento.target.value); }}
                                    value={ano}
                                >
                                    {anosDisponiveis.map((item) => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                            <div class="tw-flex tw-min-w-[220px] tw-max-w-[680px] tw-flex-1 tw-items-center tw-gap-3">
                                <label for="filtro-emplacamento" class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-slate-300">
                                    Pesquisa:
                                </label>
                                <input
                                    id="filtro-emplacamento"
                                    type="search"
                                    class="form-control tw-h-11 tw-flex-1 tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white/95 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 placeholder:tw-text-slate-400 focus:tw-border-indigo-400 focus:tw-ring-2 focus:tw-ring-indigo-300 dark:tw-border-slate-700 dark:tw-!bg-slate-900/70 dark:tw-text-slate-100 dark:placeholder:tw-text-slate-500 dark:focus:tw-ring-indigo-400/40"
                                    placeholder="Filtrar por filiado, placa, renavam..."
                                    value={filtroTexto}
                                    onChange={evento => setFiltroTexto(evento.target.value)}
                                />
                            </div>
                            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-text-sm tw-font-semibold tw-text-slate-600 dark:tw-text-white">
                                <span class="tw-flex tw-items-center tw-gap-1">
                                    <span>Taxa Total:</span>
                                    <span class="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{formatarMoedaBRL(totalTaxaFiltrado)}</span>
                                </span>
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-3 tw-ml-auto">
                                <ColumnToggleDropdown
                                    colunas={colunasBase}
                                    visibilidade={colunasVisiveis}
                                    onToggle={toggleColuna}
                                    label="Colunas"
                                    align="end"
                                />
                                <button
                                    type="button"
                                    class="btn-export-excel dark:tw-shadow-none tw-min-w-[160px]"
                                    onClick={handleExportarExcel}
                                >
                                    <span class="material-symbols-outlined">cloud_download</span>
                                    <span>Exportar Excel</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row row-padding-conteudo">
                <div class="col-12 mobile-hide tablet-hide">
                    <div class="admin-emplacar-table-wrapper table-responvive">
                        <DataTable
                            columns={colunasTabela}
                            data={dadosFiltrados}
                            getRowId={(row, index) => row.ve101_key_public || index}
                            className="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/95 tw-shadow-xl tw-backdrop-blur-sm dark:tw-border-slate-800/70 dark:tw-bg-slate-900/70 dark:tw-shadow-[0_24px_48px_rgba(8,15,32,0.45)]"
                        />
                    </div>
                </div>
            </div>
            <Paginacao esconder={true} />
        </div>
    </div>
    )

}

export default TabelaRelatorioEmplacamento;
